import React from 'react';
import { CAP_IMAGE_STORAGE_KEY, CAP_NOTES_STORAGE_KEY } from './constants';
import { normalizeForMatch } from './textUtils';

// ---------------------------------------------------------------------------
// Capability screenshot -> AI-generated usage image store
// ---------------------------------------------------------------------------
// Images are keyed by the normalized capability name so the epic table can
// render them regardless of markdown formatting. Backed by localStorage
// (best-effort) so they survive a reload within the session.

const loadStoredCapabilityImages = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(CAP_IMAGE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const capabilityImages = new Map<string, string>(Object.entries(loadStoredCapabilityImages()));
const capabilityImageListeners = new Set<() => void>();

const persistCapabilityImages = () => {
  try {
    const obj: Record<string, string> = {};
    capabilityImages.forEach((v, k) => { obj[k] = v; });
    localStorage.setItem(CAP_IMAGE_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Storage quota exceeded (data URLs are large) — keep the in-memory copy.
  }
};

export const setCapabilityImage = (capability: string, dataUrl: string) => {
  capabilityImages.set(normalizeForMatch(capability), dataUrl);
  persistCapabilityImages();
  capabilityImageListeners.forEach(fn => fn());
};

export const getCapabilityImage = (capability: string): string | undefined =>
  capabilityImages.get(normalizeForMatch(capability));

// Re-renders a table cell when its capability's generated image arrives.
export const useCapabilityImage = (capability: string): string | undefined => {
  const [img, setImg] = React.useState<string | undefined>(() => getCapabilityImage(capability));
  React.useEffect(() => {
    const listener = () => setImg(getCapabilityImage(capability));
    capabilityImageListeners.add(listener);
    listener();
    return () => { capabilityImageListeners.delete(listener); };
  }, [capability]);
  return img;
};

// ---------------------------------------------------------------------------
// Capability notes -> user-editable text stored per capability
// ---------------------------------------------------------------------------
// The Notes column of the epic table is an editable text box. Whatever the user
// types is keyed by the normalized capability name and persisted to localStorage
// so it survives table re-renders (rechecks/add-to-table) and reloads.

const loadStoredCapabilityNotes = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(CAP_NOTES_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const capabilityNotes = new Map<string, string>(Object.entries(loadStoredCapabilityNotes()));

const persistCapabilityNotes = () => {
  try {
    const obj: Record<string, string> = {};
    capabilityNotes.forEach((v, k) => { obj[k] = v; });
    localStorage.setItem(CAP_NOTES_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Best-effort persistence — keep the in-memory copy on quota errors.
  }
};

export const getCapabilityNote = (capability: string): string | undefined =>
  capabilityNotes.get(normalizeForMatch(capability));

export const setCapabilityNote = (capability: string, note: string) => {
  capabilityNotes.set(normalizeForMatch(capability), note);
  persistCapabilityNotes();
};

// Wipes the capability image + notes stores from memory and localStorage, then
// notifies image listeners so any rendered cells re-render. Called when a new
// session starts.
export const clearCapabilityStore = () => {
  capabilityImages.clear();
  capabilityNotes.clear();
  try {
    localStorage.removeItem(CAP_IMAGE_STORAGE_KEY);
    localStorage.removeItem(CAP_NOTES_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
  capabilityImageListeners.forEach(fn => fn());
};
